import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  TextField
} from '@mui/material';
import {
  AutoAwesome,
  Description,
  UploadFile,
  ArrowForward,
  ArrowBack,
  CloudUpload,
  Download,
  Delete
} from '@mui/icons-material';
import DocumentUpload from './DocUpload';
import GeneratedOutput from './GeneratedOutput';
import { useFrdGenerator } from './useFRD';

// TRD Generator Component (Existing functionality)
const TRDGenerator = ({ onBack }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [userPrompt, setUserPrompt] = useState('');
  const steps = ['Upload FRD', 'Provide Instructions', 'Generate TRD', 'Review & Download'];

  const {
    uploadedFile,
    setUploadedFile,
    uploadedDocument,
    generatedData,
    loading,
    error,
    setError,
    uploadDocument,
    generateDocumentation,
    downloadDocument,
    downloadTRDDocx,
    clearAll
  } = useFrdGenerator();

  const handleNext = async () => {
    try {
      if (activeStep === 0 && uploadedFile) {
        const document = await uploadDocument();
        if (document && document.id) {
          setActiveStep((prev) => prev + 1);
        }
      } else if (activeStep === 1) {
        setActiveStep((prev) => prev + 1);
      } else if (activeStep === 2 && uploadedDocument) {
        await generateDocumentation(uploadedDocument.id, userPrompt);
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      //console.error('Step progression error:', err);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setUserPrompt('');
    clearAll();
  };

  const handleFileUpload = (file) => {
    setUploadedFile(file);
  };

  const handleDownloadTRD = async () => {
    await downloadDocument(uploadedDocument.id);
  };
  
const handleDownloadDOCX = async () => {
  await downloadTRDDocx(uploadedDocument.id); // New DOCX download
};

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{ mb: 3 }}
      >
        Back to Selection
      </Button>

      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ color: '#333' }}>
          <AutoAwesome sx={{ mr: 2, verticalAlign: 'middle', color: '#1976d2' }} />
          AI TRD Generator
        </Typography>
        <Typography sx={{ opacity: 0.85, color: '#555' }}>
          Upload Functional Requirements Document and generate Technical Requirements Document automatically
        </Typography>
      </Paper>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity={error.severity || (typeof error === 'string' && error.startsWith('⚠️') ? 'warning' : 'error')}
          sx={{ mb: 3, borderRadius: '8px', alignItems: 'center' }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {error?.upgrade_required && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI TRD Generation" } }))}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Upgrade Plan
                </Button>
              )}
              <Button color="inherit" size="small" onClick={() => setError(null)}>
                Dismiss
              </Button>
            </Box>
          }
        >
          {error.message || error}
        </Alert>
      )}

      {/* Step Content */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {activeStep === 0 && (
            <DocumentUpload
              onFileUpload={handleFileUpload}
              uploadedFile={uploadedFile}
              loading={loading}
            />
          )}

          {activeStep === 1 && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AutoAwesome color="secondary" />
                Additional Instructions (Optional)
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Provide specific instructions or focus areas for the AI to consider when generating the Technical Requirements Document.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                label="Your Instructions"
                placeholder="Examples:
- Focus on security requirements and authentication
- Emphasize scalability and performance
- Include detailed API specifications
- Add specific compliance requirements (GDPR, HIPAA, etc.)
- Focus on mobile responsiveness
- Include specific technology preferences"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  💡 Tips for better results:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <ul>
                    <li>Be specific about technical preferences</li>
                    <li>Mention any compliance or regulatory requirements</li>
                    <li>Specify focus areas (security, performance, UX, etc.)</li>
                    <li>Include any technology stack preferences</li>
                    <li>Mention specific modules or features to emphasize</li>
                  </ul>
                </Typography>
              </Paper>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {loading ? (
                <>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    AI is Generating Technical Documentation...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This may take a few moments as we analyze your FRD and create comprehensive technical requirements
                  </Typography>
                  
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip label="Processing FRD" color="secondary" variant="outlined" />
                    <Chip label="Applying Your Instructions" color="secondary" variant="outlined" />
                    <Chip label="Generating TRD" color="secondary" variant="outlined" />
                    <Chip label="Finalizing Documentation" color="secondary" variant="outlined" />
                  </Box>
                </>
              ) : (
                <Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Ready to generate Technical Requirements Document
                  </Typography>
                  {userPrompt && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight="bold">
                        Your instructions will be applied:
                      </Typography>
                      <Typography variant="body2">
                        {userPrompt}
                      </Typography>
                    </Paper>
                  )}
                  {/* <Button 
                    variant="contained" 
                    color="pri"
                    onClick={handleNext}
                    sx={{ mt: 2 }}
                    startIcon={<AutoAwesome />}
                    size="large"
                  >
                    Generate TRD with AI
                  </Button> */}
                </Box>
              )}
            </Box>
          )}

          {activeStep === 3 && generatedData && (
            <Box sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesome color="secondary" />
                  Technical Documentation Ready!
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Download />}
                    onClick={handleDownloadTRD}
                    disabled={!uploadedDocument}
                  >
                    Download TRD (PDF)
                  </Button>
                    <Button
    variant="contained"
    color="primary"
    startIcon={<Description />}
    onClick={handleDownloadDOCX}
    disabled={!uploadedDocument}
  >
    Download DOCX
  </Button> */}
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: '100%', border: 1, borderColor: 'success.light' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description color="primary" />
                      Generated Document
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                      {generatedData.trd && (
                        <Chip 
                          label="Technical Requirements Document" 
                          color="success" 
                          variant="filled"
                          sx={{ fontSize: '1rem', padding: 2 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      A comprehensive technical requirements document has been generated from your FRD, including system specifications, requirements, and technical guidelines.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: '100%', border: 1, borderColor: 'secondary.light' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Download color="secondary" />
                      Download Options
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="secondary"
                        fullWidth 
                        startIcon={<Description />}
                        onClick={handleDownloadTRD}
                        size="large"
                        disabled={!uploadedDocument}
                      >
                        Download TRD (PDF)
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary"
                        fullWidth 
                        startIcon={<Description />}
                        onClick={handleDownloadDOCX}
                        size="large"
                        disabled={!uploadedDocument}
                      >
                        Download TRD (DOCX)
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                        Download the Technical Requirements Document in PDF or DOCX format
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            startIcon={<Delete />}
            disabled={loading}
          >
            Reset All
          </Button>
          
          {activeStep === 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!uploadedFile || loading}
              startIcon={<CloudUpload />}
            >
              {loading ? 'Uploading...' : 'Upload & Continue'}
            </Button>
          )}
          
          {activeStep === 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={loading}
            >
              Continue to Generate
            </Button>
          )}
          
          {activeStep === 2 && !loading && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              startIcon={<AutoAwesome />}
            >
              Generate TRD
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// FRD Generator Component (NEW - AI FRD Generator)
const FRDGenerator = ({ onBack }) => {
  const [projectTitle, setProjectTitle] = useState('');
  const [requirementsPrompt, setRequirementsPrompt] = useState('');
  const [generatedFRD, setGeneratedFRD] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  const { generateFRD, downloadFRD, loading } = useFrdGenerator();

  const handleGenerate = async () => {
    if (!projectTitle.trim()) {
      setError('Please enter a project title');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await generateFRD(projectTitle, requirementsPrompt);
      setGeneratedFRD(result);
    } catch (err) {
      setError(err.message || 'Failed to generate FRD');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (generatedFRD) {
      await downloadFRD(generatedFRD.title, generatedFRD.content);
    }
  };

  const handleReset = () => {
    setGeneratedFRD(null);
    setProjectTitle('');
    setRequirementsPrompt('');
    setError(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{ mb: 3 }}
      >
        Back to Selection
      </Button>

      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ color: '#333' }}>
          <AutoAwesome sx={{ mr: 2, verticalAlign: 'middle', color: '#1976d2' }} />
          AI FRD Generator
        </Typography>
        <Typography sx={{ opacity: 0.85, color: '#555' }}>
          Generate a comprehensive Functional Requirements Document using AI
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!generatedFRD ? (
        // Input Form
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Enter Project Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide a project title and optional requirements description. The AI will generate a complete FRD.
            </Typography>

            <TextField
              fullWidth
              label="Project Title *"
              placeholder="e.g., E-Commerce Platform, HR Management System, Mobile Banking App"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              sx={{ mb: 3 }}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Requirements Description (Optional)"
              placeholder="Describe your project requirements, features, and expectations. Example:
- User authentication and authorization
- Product catalog with search and filters
- Shopping cart and checkout process
- Order management and tracking
- Admin dashboard for managing products and orders"
              value={requirementsPrompt}
              onChange={(e) => setRequirementsPrompt(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AutoAwesome />}
                onClick={handleGenerate}
                disabled={!projectTitle.trim() || isGenerating || loading}
              >
                {isGenerating || loading ? <CircularProgress size={24} /> : 'Generate FRD'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isGenerating || loading}
              >
                Clear
              </Button>
            </Box>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                💡 <strong>Tips for better results:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ pl: 2 }}>
                <ul>
                  <li>Be specific about your project goals</li>
                  <li>List key features and functionalities</li>
                  <li>Mention target users and their needs</li>
                  <li>Include any technical constraints</li>
                </ul>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        // Generated FRD Display
        <Box>
          <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description color="primary" />
                {generatedFRD.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generated successfully! Review and download your document.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
              >
                Generate New
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleDownload}
              >
                Download as Word
              </Button>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 4,
              maxHeight: '60vh',
              overflow: 'auto',
              backgroundColor: '#fafafa',
              whiteSpace: 'pre-wrap',
              fontFamily: '"Georgia", serif',
              lineHeight: 1.8
            }}
          >
            {generatedFRD.content}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

// Main Component with Selection View
const UploadFrd = () => {
  const [view, setView] = useState('selection'); // 'selection' | 'frd' | 'trd'

  const handleSelectFRD = () => setView('frd');
  const handleSelectTRD = () => setView('trd');
  const handleBack = () => setView('selection');

  // Render Selection View
  if (view === 'selection') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid #e0e0e0',
            bgcolor: '#ffffff'
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#333' }}>
            AI Document Generator
          </Typography>
          <Typography sx={{ opacity: 0.85, color: '#555' }}>
            Choose the type of document you want to generate
          </Typography>
        </Paper>

        {/* Selection Cards */}
        <Grid container spacing={4} justifyContent="center">
          {/* FRD Generator Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      color: 'white',
                      display: 'flex'
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 32 , }} />
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    AI FRD Generator
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Build a Functional Requirements Document using AI prompts.
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Features:</strong>
                </Typography>
                <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                  <li>Enter project title and description</li>
                  <li>AI generates complete FRD</li>
                  <li>Download as Word Document (.docx)</li>
                  <li>Professional formatting</li>
                </ul>

             
              </CardContent>
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleSelectFRD}
                >
                  Start FRD Generator
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* TRD Generator Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '50%',
                      bgcolor: 'secondary.light',
                      color: 'white',
                      display: 'flex'
                    }}
                  >
                    <Description sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="secondary">
                    AI TRD Generator
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Convert an existing FRD document to a Technical Requirements Document.
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Features:</strong>
                </Typography>
                <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                  <li>Upload FRD document</li>
                  <li>Add custom instructions</li>
                  <li>AI generates TRD</li>
                </ul>

              </CardContent>
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleSelectTRD}
                >
                  Start TRD Generator
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

  
      </Container>
    );
  }

  // Render FRD Generator View
  if (view === 'frd') {
    return <FRDGenerator onBack={handleBack} />;
  }

  // Render TRD Generator View
  if (view === 'trd') {
    return <TRDGenerator onBack={handleBack} />;
  }

  return null;
};

export default UploadFrd;